// Shapes returned by /standards, plus small formatters for the change log.

export interface SettingRow {
  area: 'planning' | 'effort';
  key: string;
  label: string;
  description: string;
  unit: 'percent' | 'days' | 'count' | 'sprint' | 'date';
  min: number | null;
  max: number | null;
  value: number | string;
  default: number | string;
  is_default: boolean;
}

export interface TagRow {
  tag: string;
  role_group: string;
  description: string | null;
  used_90d: number;
}

export interface AliasRow {
  alias: string;
  tag: string;
}

export interface StandardsData {
  settings: SettingRow[];
  tags: TagRow[];
  aliases: AliasRow[];
}

export interface ChangeRow {
  id: string;
  area: 'planning' | 'effort' | 'tags' | 'naming' | 'flow';
  item: string;
  action: 'set' | 'reset' | 'add' | 'remove';
  before: unknown;
  after: unknown;
  changed_by: string | null;
  changed_at: string;
}

export const ROLE_GROUP_LABEL: Record<string, string> = {
  engineering: 'Engineering',
  qa: 'QA',
  product: 'Product',
  design: 'Design',
  devops: 'DevOps',
};

export const AREA_LABEL: Record<ChangeRow['area'], string> = {
  planning: 'Planning',
  effort: 'Time logging',
  tags: 'Tags',
  naming: 'Naming',
  flow: 'Flow',
};

const UNIT_SUFFIX: Record<SettingRow['unit'], string> = { percent: '%', days: ' days', count: '', sprint: '', date: '' };

export function formatSetting(s: Pick<SettingRow, 'unit'>, value: number | string): string {
  return `${value}${UNIT_SUFFIX[s.unit]}`;
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const show = (v: unknown): string => (v === null || v === undefined ? 'none' : Array.isArray(v) ? v.join(', ') || 'none' : typeof v === 'object' ? JSON.stringify(v) : String(v));

/** One plain line for a change: "Halfway target: 40 to 55", "Added the tag Pairing", "message: … to …". */
export function describeChange(c: ChangeRow): string {
  if (c.action === 'add') return `Added ${c.item}`;
  if (c.action === 'remove') return `Removed ${c.item}`;
  if (isObject(c.before) && isObject(c.after)) {
    const keys = Array.from(new Set([...Object.keys(c.before), ...Object.keys(c.after)])).filter((k) => show(c.before && (c.before as any)[k]) !== show((c.after as any)[k]));
    if (keys.length > 0) return `${c.item}: ${keys.map((k) => `${k.replace(/_/g, ' ')} ${show((c.before as any)[k])} → ${show((c.after as any)[k])}`).join('; ')}`;
  }
  return `${c.item}: ${show(c.before)} → ${show(c.after)}${c.action === 'reset' ? ' (reset to the default)' : ''}`;
}
